/**
 * The single entry point for changing game state.
 *
 *   apply(state, command, actor) → { ok: true, state, events } | { ok: false, error }
 *
 * Rules of this file:
 *   - never mutate `state`; build a new one
 *   - every accepted command bumps `seq` by exactly one
 *   - anything not yet implemented returns NOT_IMPLEMENTED (never a silent no-op)
 *   - no randomness except through the rng carried in the state
 *
 * Sequencing lives here; the content of each action lives in actions/<kind>.ts.
 * See SLICES.md for what is implemented and what is next.
 */
import { construction } from './actions/construction.js';
import { takeForumTile } from './actions/forum.js';
import { military } from './actions/military.js';
import { seaport, seaportDiscard } from './actions/seaport.js';
import { senate } from './actions/senate.js';
import { useExtraActionTile } from './extraActions.js';
import { beginQuarterEnd, chooseBonusTile, satisfyDemand } from './quarterEnd.js';
import { takeTrajanTile } from './actions/trajan.js';
import { afterAction, currentPlayer, fail, playerOf, succeed, updatePlayer, } from './internal.js';
import { MANIFESTS } from './manifests.js';
import { acceptTrajanTile, declineTrajanTile } from './trajanTiles.js';
import { distribute, isPermutationOf, trajanTileSatisfied } from './tray.js';
import { COLORS, MARKERS_PER_TRAY, TRAY_COUNT } from './types.js';
// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------
/** Advance the time marker; report whether it reached or crossed the start space. */
export function advanceTime(time, by) {
    const { position, start, length } = time;
    const distanceToStart = (((start - position) % length) + length) % length || length;
    const crossedStart = by >= distanceToStart;
    return {
        time: {
            ...time,
            position: (position + by) % length,
            crossedStartThisTurn: time.crossedStartThisTurn || crossedStart,
        },
        crossedStart,
    };
}
function actionOfTray(tray) {
    return MANIFESTS.trayActions[tray];
}
function nextPlayer(state, after = state.current) {
    const i = state.turnOrder.indexOf(after);
    return state.turnOrder[(i + 1) % state.turnOrder.length];
}
function isLastInOrder(state, player) {
    return state.turnOrder[state.turnOrder.length - 1] === player;
}
/** The seat whose decision the engine is waiting for (works on a View too). */
export function decisionMaker(state) {
    const { phase } = state;
    switch (phase.kind) {
        case 'gameOver':
            return null;
        case 'setup.arrangeMarkers':
        case 'setup.chooseTrajanTiles':
        case 'quarterEnd.demands':
        case 'quarterEnd.consulChoice':
            return phase.player;
        default:
            return state.current;
    }
}
// ---------------------------------------------------------------------------
// apply
// ---------------------------------------------------------------------------
export function apply(state, command, actor) {
    if (state.phase.kind === 'gameOver')
        return fail('GAME_OVER', 'the game is over');
    const expected = decisionMaker(state);
    if (actor !== expected)
        return fail('NOT_YOUR_TURN', `it is player ${expected}'s decision`);
    const result = dispatch(state, command, actor);
    if (!result.ok)
        return result;
    return { ...result, state: { ...result.state, seq: state.seq + 1 } };
}
function dispatch(state, command, actor) {
    switch (command.type) {
        case 'ArrangeMarkers':
            return arrangeMarkers(state, actor, command.trays);
        case 'ChooseInitialTrajanTiles':
            return chooseInitialTrajanTiles(state, actor, command.picks);
        case 'SelectTray':
            return selectTray(state, actor, command.tray, command.colorOrder);
        case 'AccomplishTrajanTile':
            if (state.phase.kind !== 'awaitTrajanDecision')
                return fail('WRONG_PHASE', 'no Trajan tile is on offer');
            return command.accept
                ? acceptTrajanTile(state, actor, state.phase.slot, command.plus2Space)
                : declineTrajanTile(state, actor, state.phase.slot);
        case 'TakeAction':
            return takeAction(state, actor, command.kind, command.payload);
        case 'SkipAction':
            return skipAction(state, actor);
        case 'DiscardCard':
            return seaportDiscard(state, actor, command.cardId, command.pile);
        case 'EndTurn':
            return endTurn(state, actor);
        case 'UseExtraActionTile':
            return useExtraActionTile(state, actor, command.tileId, command.action);
        case 'SatisfyDemand':
            return satisfyDemand(state, actor, command.demandIndex, command.tileId);
        case 'ChooseBonusTile':
            return chooseBonusTile(state, actor, command.side);
    }
}
// ---------------------------------------------------------------------------
// Setup: arrange markers (R-001), choose initial Trajan tiles (R-004)
// ---------------------------------------------------------------------------
function arrangeMarkers(state, actor, trays) {
    if (state.phase.kind !== 'setup.arrangeMarkers')
        return fail('WRONG_PHASE', 'markers are already arranged');
    if (trays.length !== TRAY_COUNT || trays.some((t) => t.length !== MARKERS_PER_TRAY)) {
        return fail('ILLEGAL', `each of the ${TRAY_COUNT} trays must hold exactly ${MARKERS_PER_TRAY} markers`);
    }
    if (!isPermutationOf(trays.flat(), [...COLORS, ...COLORS])) {
        return fail('ILLEGAL', 'the arrangement must use exactly two markers of each colour');
    }
    let next = updatePlayer(state, actor, (p) => ({ ...p, trays: trays.map((t) => [...t]) }));
    next = isLastInOrder(state, actor)
        ? {
            ...next,
            phase: { kind: 'setup.chooseTrajanTiles', player: state.turnOrder[0] },
        }
        : { ...next, phase: { kind: 'setup.arrangeMarkers', player: nextPlayer(state, actor) } };
    return succeed(next, [{ type: 'MarkersArranged', player: actor }]);
}
function chooseInitialTrajanTiles(state, actor, picks) {
    if (state.phase.kind !== 'setup.chooseTrajanTiles')
        return fail('WRONG_PHASE', 'initial Trajan tiles are already chosen');
    const rule = MANIFESTS.setup.initialTrajanTiles;
    const allowedSlots = rule.slots;
    if (picks.length !== rule.count)
        return fail('ILLEGAL', `choose exactly ${rule.count} Trajan tiles`);
    const stacks = picks.map((p) => p.stack);
    const slots = picks.map((p) => p.slot);
    if (new Set(stacks).size !== picks.length)
        return fail('ILLEGAL', `at most ${rule.maxPerCategory} tile per category`);
    if (new Set(slots).size !== picks.length || slots.some((s) => !allowedSlots.includes(s))) {
        return fail('ILLEGAL', `tiles go on slots ${allowedSlots.map((s) => s + 1).join(', ')}, one each`);
    }
    for (const st of stacks) {
        if (!Number.isInteger(st) || st < 0 || st >= state.board.trajanStacks.length)
            return fail('ILLEGAL', `no stack ${st}`);
        if ((state.board.trajanStacks[st] ?? []).length === 0)
            return fail('ILLEGAL', `stack ${st} is empty`);
    }
    const trajanStacks = state.board.trajanStacks.map((s) => [...s]);
    const trajanSlots = [...playerOf(state, actor).trajanSlots];
    const tileIds = [];
    for (const pick of picks) {
        const tile = trajanStacks[pick.stack].shift();
        if (!tile)
            return fail('ILLEGAL', `stack ${pick.stack} is empty`);
        trajanSlots[pick.slot] = tile;
        tileIds.push(tile.id);
    }
    let next = updatePlayer(state, actor, (p) => ({ ...p, trajanSlots }));
    next = { ...next, board: { ...next.board, trajanStacks } };
    const events = [{ type: 'InitialTrajanTilesChosen', player: actor, tileIds, slots }];
    if (isLastInOrder(state, actor)) {
        const first = state.turnOrder[0];
        next = { ...next, current: first, phase: { kind: 'awaitTray' } };
        events.push({ type: 'GameStarted', first }, { type: 'TurnStarted', player: first });
    }
    else {
        next = {
            ...next,
            phase: { kind: 'setup.chooseTrajanTiles', player: nextPlayer(state, actor) },
        };
    }
    return succeed(next, events);
}
// ---------------------------------------------------------------------------
// Turn step 1 + 2: rearrange markers, advance time
// ---------------------------------------------------------------------------
function selectTray(state, actor, tray, colorOrder) {
    if (state.phase.kind !== 'awaitTray')
        return fail('WRONG_PHASE', 'a tray has already been chosen this turn');
    const player = currentPlayer(state);
    const d = distribute(player.trays, tray, colorOrder);
    if (!d)
        return fail('ILLEGAL', `tray ${tray} is empty or the colour order does not match its markers`);
    const events = [
        {
            type: 'TrayMoved',
            player: actor,
            source: tray,
            target: d.target,
            moved: d.moved,
            colorOrder: d.colorOrder,
        },
    ];
    const { time, crossedStart } = advanceTime(state.time, d.moved);
    events.push({
        type: 'TimeAdvanced',
        from: state.time.position,
        to: time.position,
        by: d.moved,
        crossedStart,
    });
    let next = updatePlayer(state, actor, (p) => ({ ...p, trays: d.trays }));
    next = { ...next, time, turn: { performed: [], extraActionUsed: false } };
    // Turn step 3: is the Trajan tile beside the target tray satisfied?
    const slot = MANIFESTS.trajanSlotBesideTray[d.target];
    const tile = player.trajanSlots[slot] ?? null;
    const targetTray = d.trays[d.target] ?? [];
    const pending = [{ source: 'tray', action: actionOfTray(d.target) }];
    if (tile && trajanTileSatisfied(tile, targetTray)) {
        events.push({ type: 'TrajanTileOffered', player: actor, slot, tileId: tile.id });
        next = { ...next, pendingActions: pending, phase: { kind: 'awaitTrajanDecision', slot } };
    }
    else {
        next = {
            ...next,
            pendingActions: pending,
            phase: { kind: 'awaitAction', action: actionOfTray(d.target) },
        };
    }
    return succeed(next, events);
}
// ---------------------------------------------------------------------------
// Turn step 4: actions
// ---------------------------------------------------------------------------
function takeAction(state, actor, kind, payload) {
    if (state.phase.kind !== 'awaitAction')
        return fail('WRONG_PHASE', 'no action is pending');
    if (state.phase.action !== kind)
        return fail('ILLEGAL', `the pending action is ${state.phase.action}, not ${kind}`);
    const result = perform(state, actor, kind, payload);
    if (!result.ok)
        return result;
    const turn = { ...result.state.turn, performed: [...result.state.turn.performed, kind] };
    return { ...result, state: { ...result.state, turn } };
}
function perform(state, actor, kind, payload) {
    switch (kind) {
        case 'trajan': {
            const p = payload;
            if (!p || typeof p.stack !== 'number')
                return fail('ILLEGAL', 'Trajan action needs { stack }');
            return takeTrajanTile(state, actor, p.stack);
        }
        case 'forum':
            return takeForumTile(state, actor, payload);
        case 'military':
            return military(state, actor, payload);
        case 'seaport':
            return seaport(state, actor, payload);
        case 'construction':
            return construction(state, actor, payload);
        case 'senate':
            return senate(state, actor);
    }
}
function skipAction(state, actor) {
    if (state.phase.kind !== 'awaitAction')
        return fail('WRONG_PHASE', 'no action to skip');
    const skipped = state.pendingActions[0];
    if (!skipped || !('action' in skipped))
        return fail('ILLEGAL', 'pending action queue is inconsistent');
    return succeed(afterAction(state), [
        { type: 'ActionSkipped', player: actor, action: skipped.action },
    ]);
}
// ---------------------------------------------------------------------------
// End of turn, round and quarter boundaries
// ---------------------------------------------------------------------------
function endTurn(state, actor) {
    if (state.phase.kind !== 'awaitAction' && state.phase.kind !== 'awaitExtraAction') {
        return fail('WRONG_PHASE', 'the turn cannot end from this step');
    }
    const events = [];
    if (state.phase.kind === 'awaitAction') {
        for (const p of state.pendingActions) {
            if ('action' in p)
                events.push({ type: 'ActionSkipped', player: actor, action: p.action });
        }
    }
    events.push({ type: 'TurnEnded', player: actor });
    let next = { ...state, pendingActions: [] };
    const M = MANIFESTS.timeTrack;
    if (state.time.crossedStartThisTurn) {
        const { round, quarter } = state.time;
        events.push({ type: 'RoundEnded', quarter, round });
        if (round < M.roundsPerQuarter) {
            let demands = next.demands;
            let demandSupply = next.hidden.demandSupply;
            if (M.demandsRevealedAfterRounds.includes(round)) {
                const revealed = demandSupply[0];
                if (!revealed)
                    return fail('ILLEGAL', 'demand supply is empty');
                demandSupply = demandSupply.slice(1);
                demands = [...demands, revealed];
                events.push({ type: 'DemandRevealed', icon: revealed.icon });
            }
            next = {
                ...next,
                demands,
                hidden: { ...next.hidden, demandSupply },
                time: { ...next.time, round: round + 1, crossedStartThisTurn: false },
            };
        }
        else {
            // Fourth crossing: the quarter ends (demands, senate balance, refill).
            return beginQuarterEnd(next, actor, events);
        }
    }
    const upcoming = nextPlayer(next);
    next = { ...next, current: upcoming, phase: { kind: 'awaitTray' } };
    events.push({ type: 'TurnStarted', player: upcoming });
    return succeed(next, events);
}
//# sourceMappingURL=reducer.js.map